import {
  ContractCategory,
  ContractFileCategory,
  ContractFileInitItem,
  ContractListItemResponse,
  ContractNoteFileInitResponse,
  ContractStatus,
  CreateContractRequest,
  UpdateContractBody,
} from 'compliance-api/generated';
import { FormikProvider, useFormik } from 'formik';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { api } from '@/modules/common/api/index.ts';
import { ContractAddingLoadingScreen } from '@/modules/admin-dashboard/components/contractManagement/ContractAddingLoadingScreen.tsx';
import { ContractStatusBadge } from '@/modules/admin-dashboard/components/contractManagement/ContractStatusBadge.tsx';
import PartyModal from '@/modules/admin-dashboard/components/contractManagement/PartyModal.tsx';
import { contractCategoryOptions } from '@/modules/admin-dashboard/components/contractManagement/contractCategory.ts';
import { contractStatusOptions } from '@/modules/admin-dashboard/components/contractManagement/contractStatus.ts';
import {
  useCompleteContractUploadMutation,
  useCreateContractMutation,
  useUpdateContractMutation,
} from '@/modules/admin-dashboard/queries/contract-management.ts';
import { adminDashboardPaths } from '@/modules/admin-dashboard/routes.tsx';
import {
  BaseModalProps,
  Button,
  Dropzone,
  ModalContentWrapper,
  PopoverMenu,
  Select,
  SelectOption,
  SelectPaginate,
  SelectPaginateOption,
  TextInputField,
} from 'ui';
import { ChevronDownIcon, PlusIcon, TrashGrayIcon } from 'ui/icons';
import { fixNameEnum } from 'ui/utils';

type SelectedContractFile = {
  file: File;
  file_category?: ContractFileCategory;
};

type ContractFormValues = {
  id?: number;
  name: string;
  status: ContractStatus;
  category?: ContractCategory | null;
  counterparty_party_options: SelectPaginateOption<number>[];
  files: SelectedContractFile[];
};

interface ContractModalProps extends BaseModalProps {
  contract?: ContractListItemResponse;
}

const contractFileCategoryOptions: SelectOption<ContractFileCategory | null>[] = [
  { value: null, label: 'Auto' },
  ...Object.values(ContractFileCategory).map(category => ({
    value: category,
    label: fixNameEnum(category),
  })),
];

const validationSchema = yup.object<ContractFormValues>({
  name: yup.string().trim().required('Name is required').max(255),
  status: yup.mixed<ContractStatus>().required('Status is required'),
  category: yup.mixed<ContractCategory>().nullable(),
  counterparty_party_options: yup.array().default([]),
  files: yup.array().default([]),
});

const getInitialValues = (contract?: ContractListItemResponse): ContractFormValues => ({
  id: contract?.id,
  name: contract?.contract_name || '',
  status: contract?.status || ContractStatus.Draft,
  category: contract?.category ?? null,
  counterparty_party_options: contract?.parties?.map(selectPartyOption) || [],
  files: [],
});

const fileToContractFileInitItem = ({
  file,
  file_category,
}: SelectedContractFile): ContractFileInitItem => {
  const payload: ContractFileInitItem = {
    file_name: file.name,
    mime_type: file.type || 'application/octet-stream',
    file_size: file.size,
  };

  if (file_category) {
    payload.file_category = file_category;
  }

  return payload;
};

const uploadContractFiles = async (uploads: ContractNoteFileInitResponse[], files: File[]) => {
  const storageItemIds = await Promise.all(
    uploads.map(async (upload, index) => {
      const file = files[index];
      if (!file) return undefined;

      const formData = new FormData();
      Object.entries(upload.presigned_upload_fields || {}).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append('file', file);

      const response = await fetch(upload.presigned_upload_url, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Failed to upload ${file.name}`);
      }

      return upload.storage_item_id;
    }),
  );

  return storageItemIds.filter(Boolean) as string[];
};

const selectPartyOption = (party: {
  id: number;
  name?: string;
  legal_name?: string;
}): SelectPaginateOption<number> => ({
  value: party.id,
  label: party.name || party.legal_name || String(party.id),
});

const getContractNameFromFile = (fileName: string) => {
  const lastDotIndex = fileName.lastIndexOf('.');
  if (lastDotIndex <= 0) {
    return fileName;
  }

  return fileName.slice(0, lastDotIndex);
};

const formatFileSize = (size: number) => {
  if (size < 1024 * 1024) {
    return `${Math.max(1, Math.round(size / 1024))} KB`;
  }

  return `${Math.round(size / (1024 * 1024))} MB`;
};

const ContractModal = ({ contract, open, onClose }: ContractModalProps) => {
  const navigate = useNavigate();
  const [isFilesUploading, setIsFilesUploading] = useState(false);
  const [isPartyModalOpen, setIsPartyModalOpen] = useState(false);
  const [cacheUniq, setCacheUniq] = useState(Math.random());

  const formik = useFormik<ContractFormValues>({
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    enableReinitialize: true,
    initialValues: getInitialValues(contract),
    onSubmit: async values => {
      if (contract?.id) {
        const payload: UpdateContractBody = {
          name: values.name.trim(),
          status: values.status,
          category: values.category ?? undefined,
        };

        await updateContract({
          id: contract.id,
          data: payload,
        });

        onClose();
        return;
      }

      const payload: CreateContractRequest = {
        name: values.name.trim(),
        status: values.status,
        category: values.category ?? undefined,
        counterparty_party_ids: values.counterparty_party_options.map(({ value }) => value),
        files: values.files.map(fileToContractFileInitItem),
      };

      let createdContractId: number | undefined;
      setIsFilesUploading(values.files.length > 0);
      try {
        const response = await createContract(payload);

        if (values.files.length > 0 && response.uploads?.length) {
          const storageItemIds = await uploadContractFiles(
            response.uploads,
            values.files.map(({ file }) => file),
          );

          for (const storage_item_id of storageItemIds) {
            await completeContractUpload({
              contractId: response.contract.id,
              data: { storage_item_id },
            });
          }
        }

        createdContractId = response.contract.id;
      } finally {
        setIsFilesUploading(false);
      }

      if (createdContractId) {
        onClose();
        navigate({
          pathname: adminDashboardPaths.dynamic.contract(createdContractId),
        });
      }
    },
  });

  const { mutateAsync: createContract, isLoading: isCreateLoading } = useCreateContractMutation(
    formik.setErrors,
  );
  const { mutateAsync: updateContract, isLoading: isUpdateLoading } = useUpdateContractMutation(
    formik.setErrors,
  );
  const { mutateAsync: completeContractUpload, isLoading: isUploadCompleting } =
    useCompleteContractUploadMutation();

  const isEditMode = !!contract?.id;
  const isLoading = isCreateLoading || isUpdateLoading || isUploadCompleting || isFilesUploading;

  const handleChangeSelect = useCallback(
    (field: keyof ContractFormValues) => async (value: unknown) => {
      await formik.setFieldValue(field, value);
      formik.setFieldError(field, '');
    },
    [formik],
  );

  const handleAddNewParty = useCallback(() => {
    setIsPartyModalOpen(true);
  }, []);

  const handlePartyCreated = useCallback(
    (party: { id: number; name?: string; legal_name?: string }) => {
      const option = selectPartyOption(party);
      const currentOptions = formik.values.counterparty_party_options || [];
      if (currentOptions.some(item => item.value === option.value)) {
        return;
      }
      void formik.setFieldValue('counterparty_party_options', [...currentOptions, option]);
      setCacheUniq(Math.random());
    },
    [formik],
  );

  const handleFileAccepted = useCallback(
    (files: File[]) => {
      void formik.setFieldValue('files', [
        ...formik.values.files,
        ...files.map(file => ({ file })),
      ]);
      formik.setFieldError('files', '');

      if (!formik.values.name.trim() && files[0]) {
        void formik.setFieldValue('name', getContractNameFromFile(files[0].name));
      }
    },
    [formik],
  );

  const handleChangeFileCategory = useCallback(
    (fileIndex: number, fileCategory: ContractFileCategory | null) => {
      void formik.setFieldValue(
        'files',
        formik.values.files.map((selectedFile, index) =>
          index === fileIndex
            ? { ...selectedFile, file_category: fileCategory || undefined }
            : selectedFile,
        ),
      );
      formik.setFieldError('files', '');
    },
    [formik],
  );

  const handleRemoveFile = useCallback(
    (fileIndex: number) => {
      void formik.setFieldValue(
        'files',
        formik.values.files.filter((_, index) => index !== fileIndex),
      );
      formik.setFieldError('files', '');
    },
    [formik],
  );

  const handleSelectPartyOption = useCallback(
    async (value: SelectPaginateOption<number>[] | SelectPaginateOption<number> | null) => {
      await formik.setFieldValue('counterparty_party_options', Array.isArray(value) ? value : []);
      formik.setFieldError('counterparty_party_options', '');
    },
    [formik],
  );

  const handleLoadParties = useCallback(async (page: number, search?: string) => {
    const response = await api.v1.getPartiesMinimalV1ContractsPartiesMinimalGet({
      page,
      page_size: 20,
      search: search || undefined,
    });

    return response.data.data.map(selectPartyOption);
  }, []);

  const statusMenuItems = useMemo(
    () =>
      contractStatusOptions.map(({ value }) => ({
        label: <ContractStatusBadge status={value} />,
        onClick: () => {
          void formik.setFieldValue('status', value);
          formik.setFieldError('status', '');
        },
      })),
    [formik],
  );

  const selectedFiles = formik.values.files;

  if (isLoading) {
    return (
      <ModalContentWrapper
        title=""
        open={open}
        onClose={onClose}
        showClose={false}
        classes={{
          dialogPanel: 'max-w-[560px]',
          children: 'p-0',
          titleWrapper: 'hidden',
        }}
      >
        <ContractAddingLoadingScreen className="py-10" />
      </ModalContentWrapper>
    );
  }

  return (
    <ModalContentWrapper
      title={isEditMode ? 'Edit contract' : 'Add new contract'}
      open={open}
      onClose={onClose}
      classes={{ dialogPanel: 'max-w-[560px]' }}
    >
      <FormikProvider value={formik}>
        <div className="space-y-8">
          <div className="flex items-center gap-3">
            <PopoverMenu
              items={statusMenuItems}
              button={
                <div className="flex items-center gap-2">
                  <ContractStatusBadge status={formik.values.status} />
                  <img src={ChevronDownIcon} alt="" className="h-4 w-4 shrink-0" />
                </div>
              }
              classes={{
                container: 'relative z-50 w-fit',
                panel: 'z-50 w-[220px] rounded-lg border border-gray-200 !bg-white p-1 shadow-lg',
              }}
            />
          </div>

          <div className="space-y-6">
            <TextInputField name="name" label="Name" />

            <Select
              label="Category (optional)"
              options={contractCategoryOptions}
              value={formik.values.category ?? null}
              onChange={handleChangeSelect('category')}
              isClearable
              showBorderBottom
            />

            <SelectPaginate
              cacheUniqs={[cacheUniq]}
              label="Counter party (optional)"
              isMulti
              value={formik.values.counterparty_party_options}
              onChange={handleSelectPartyOption}
              onAsync={handleLoadParties}
              isClearable
              disabled={isEditMode}
              addedLastOption={
                !isEditMode
                  ? {
                      label: (
                        <div className="flex items-center gap-2">
                          <img src={PlusIcon} alt="" className="h-4 w-4" />
                          <span>Add new Party</span>
                        </div>
                      ),
                      onClick: handleAddNewParty,
                    }
                  : undefined
              }
            />

            {!isEditMode && (
              <Dropzone
                multiple
                maxFiles={10}
                value=""
                uploadText={
                  selectedFiles.length
                    ? 'Add another document'
                    : 'You can just drag & drop your files and we will extract all these fields and parties for you'
                }
                uploadTextClassName="max-w-[420px] whitespace-normal text-left"
                acceptFormat={['doc', 'pdf']}
                loading={isFilesUploading}
                onFileAccepted={handleFileAccepted}
                onFileClear={() => {
                  void formik.setFieldValue('files', []);
                }}
              />
            )}
          </div>

          {selectedFiles.length > 0 && !isEditMode && (
            <div className="space-y-3">
              <div className="text-base font-medium text-gray-900">Uploaded files</div>
              <div className="space-y-2">
                {selectedFiles.map(({ file, file_category }, index) => {
                  const extension = file.name.split('.').pop()?.toUpperCase() || 'FILE';

                  return (
                    <div
                      key={`${file.name}_${file.size}_${index}`}
                      className="flex items-center gap-4 rounded border border-gray-200 bg-white px-4 py-3"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-base font-medium text-gray-900">
                          {file.name}
                        </div>
                        <div className="mt-1 text-sm leading-5 text-gray-500">
                          {extension} <span className="mx-1 text-gray-300">|</span>{' '}
                          {formatFileSize(file.size)}
                        </div>
                      </div>

                      <Select<ContractFileCategory | null>
                        options={contractFileCategoryOptions}
                        value={file_category || null}
                        onChange={value => handleChangeFileCategory(index, value)}
                        className="w-[152px] shrink-0"
                        withBorder
                        showBorderBottom={false}
                      />

                      <button
                        type="button"
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded hover:bg-gray-100"
                        onClick={() => handleRemoveFile(index)}
                        aria-label={`Remove ${file.name}`}
                      >
                        <img src={TrashGrayIcon} alt="" className="h-5 w-5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <Button disabled={isLoading} loading={isLoading} onClick={formik.submitForm}>
            Confirm
          </Button>
        </div>
      </FormikProvider>

      <PartyModal
        open={isPartyModalOpen}
        onClose={() => setIsPartyModalOpen(false)}
        onSuccess={party => {
          handlePartyCreated(party);
          setIsPartyModalOpen(false);
        }}
      />
    </ModalContentWrapper>
  );
};

export default ContractModal;
